import React, { useState, useEffect } from 'react';
import { Pill, View, Text } from '@instructure/ui';
import { formatDuration } from '../../../helpers/utilities';
import { Handle } from 'reactflow';
import { getStore } from '../../../stores/StoreContext';
import moment from 'moment';

/**
 * Custom node component for the Journey steps
 * Shows the title of the step and a time pill in the lower right
 * for completed steps or "Current" for the active step
 */
function CustomNode({ data }) {
  const { label, stepData, isCurrentStep } = data;
  const [elapsedTime, setElapsedTime] = useState(0);
  const PlayerStore = getStore('playerStore');
  const ExamSessionStore = getStore('examSessionStore');
  // Handle node click
  const handleNodeClick = (event) => {
    // Prevent event from bubbling up to the ReactFlow onNodeClick
    event.stopPropagation();
    if (stepData.startTime) {
      // Convert to moment object if it's a string
      const timeAsMoment = moment.utc(stepData.startTime);

      // Get the time between the start of the session and the target time
      const startTime = moment.utc(
        ExamSessionStore.sessionData.recordingStartDate
      );
      // Calculate difference in seconds
      const time = Math.abs(startTime.diff(timeAsMoment, 'seconds'));

      const secondsBefore = 5;
      const timeToJump = time - secondsBefore;

      if (timeToJump >= 0) {
        PlayerStore.jumpTo(timeToJump);
      }
    }
  };

  // Calculate duration if the step has both start and completion times
  const showDuration = stepData?.startTime && stepData?.completedTime;
  const showCurrentPill =
    stepData?.startTime && !stepData?.completedTime && isCurrentStep;

  // Calculate duration in seconds
  const getDuration = () => {
    if (!showDuration) return null;

    const startTime = new Date(stepData.startTime);
    const endTime = new Date(stepData.completedTime);
    const durationSeconds = Math.floor((endTime - startTime) / 1000);

    return durationSeconds;
  };

  // Set up timer for current step to show elapsed time
  useEffect(() => {
    let intervalId;

    if (showCurrentPill && stepData?.startTime) {
      // Calculate initial elapsed time
      const startTime = new Date(stepData.startTime);
      const now = new Date();
      const initialElapsedSeconds = Math.floor((now - startTime) / 1000);
      setElapsedTime(initialElapsedSeconds);

      // Update elapsed time every second
      intervalId = setInterval(() => {
        setElapsedTime((prevTime) => prevTime + 1);
      }, 1000);
    }

    // Clean up interval on unmount
    return () => {
      if (intervalId) {
        clearInterval(intervalId);
      }
    };
  }, [showCurrentPill, stepData?.startTime]);

  const duration = getDuration();

  return (
    <View
      as="div"
      background={
        isCurrentStep ? 'brand' : showDuration ? 'success' : 'primary'
      }
      padding="small"
      borderWidth="small"
      borderRadius="medium"
      borderColor={
        isCurrentStep ? 'brand' : showDuration ? 'success' : 'secondary'
      }
      width="100%"
      height="100%"
      position="relative"
      onClick={handleNodeClick}
      cursor={showDuration || isCurrentStep ? 'pointer' : 'default'}
      themeOverride={{
        hoverBackgroundColor: isCurrentStep
          ? '#0374B5'
          : showDuration
            ? '#00ac18'
            : '#eee',
      }}
    >
      {/* Source handle (top) */}
      <Handle
        type="source"
        position="bottom"
        style={{ opacity: 0 }}
        isConnectable={false}
      />

      {/* Target handle (bottom) */}
      <Handle
        type="target"
        position="top"
        style={{ opacity: 0 }}
        isConnectable={false}
      />

      <View
        as="div"
        padding="none"
        width="180px"
        withVisualDebug={false}
        textAlign="center"
      >
        <Text size="x-small" weight={isCurrentStep ? 'bold' : 'normal'}>
          {label}
        </Text>
      </View>

      {/* Time pill positioned at the bottom right of the node */}
      {(showDuration || showCurrentPill) && (
        <View
          as="div"
          position="absolute"
          insetInlineEnd="5px"
          insetBlockEnd="-12px"
        >
          <Pill
            color={showCurrentPill ? 'primary' : 'success'}
            margin="none"
            size="small"
            themeOverride={{
              textFontSize: '10px',
            }}
          >
            {showCurrentPill
              ? formatDuration(elapsedTime)
              : formatDuration(duration)}
          </Pill>
        </View>
      )}
    </View>
  );
}

export default CustomNode;
